/**
 * Sticky Header Component
 * 스크롤 동작에 따른 헤더 표시/숨김 + 드롭다운 메뉴 제어
 */
class StickyHeader extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.header = document.querySelector('.section-header');
        this.headerIsAlwaysSticky =
            this.getAttribute('data-sticky-type') === 'always' ||
            this.getAttribute('data-sticky-type') === 'reduce-logo-size';
        this.headerBounds = {};

        this.setHeaderHeight();
        window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

        if (this.headerIsAlwaysSticky) {
            this.header.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => this.preventReveal = true;

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, false);

        this.createObserver();

        // 드롭다운 메뉴 초기화
        this.initDropdownMenu();
    }

    setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
        let observer = new IntersectionObserver((entries) => {
            this.headerBounds = entries[0].intersectionRect;
            observer.disconnect();
        });
        observer.observe(this.header);
    }

    onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.header.classList.add('scrolled-past-header');
            if (this.preventHide) return;
            requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
            this.header.classList.add('scrolled-past-header');
            if (!this.preventReveal) {
                requestAnimationFrame(this.reveal.bind(this));
            } else {
                window.clearTimeout(this.isScrolling);
                this.isScrolling = setTimeout(() => {
                    this.preventReveal = false;
                }, 66);
                requestAnimationFrame(this.hide.bind(this));
            }
        } else if (scrollTop <= this.headerBounds.top) {
            this.header.classList.remove('scrolled-past-header');
            requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
    }

    hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        this.closeAllDropdowns();
        this.closeMenuDisclosure();
    }

    reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
        this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
        this.disclosures.forEach(disclosure => disclosure.close());
    }

    // 통합된 드롭다운 메뉴 제어
    initDropdownMenu() {
        this.shopBtn = this.header.querySelector('.shop-btn');
        this.headerBg = this.header.querySelector('.header');
        this.lnb = this.header.querySelector('.lnb');
        this.dBtns = this.header.querySelectorAll('.gnb-item--d');
        this.notice = this.header.querySelector('.lnb-notice');

        // if (!this.shopBtn || !this.lnb || !this.notice || !this.dBtns.length) return;
        if (!this.shopBtn || !this.lnb) return;

        const isHoverCapable = window.matchMedia('(hover: hover)').matches;

        // 이벤트 전파 차단
        this.lnb.addEventListener('click', e => e.stopPropagation());
        this.notice.addEventListener('click', e => e.stopPropagation());

        if (!isHoverCapable) {
            // 모바일: 클릭 토글
            this.shopBtn.addEventListener('pointerdown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const willOpen = !this.lnb.classList.contains('is-open');
                this.closeNotice();
                willOpen ? this.openLnb() : this.closeLnb();
            });

            this.dBtns.forEach((btn) => {
                btn.addEventListener('pointerdown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const willOpen = !this.notice.classList.contains('is-open') ||
                        !btn.classList.contains('is-open');
                    this.closeLnb();
                    willOpen ? this.openNotice(btn) : this.closeNotice();
                });
            });
        } else {
            // 데스크톱: 호버
            this.shopBtn.addEventListener('mouseenter', () => this.openLnb());
            this.dBtns.forEach((btn) => {
                btn.addEventListener('mouseenter', () => this.openNotice(btn));
            });

            let leaveTimer = null;
            this.header.addEventListener('pointerleave', () => {
                clearTimeout(leaveTimer);
                leaveTimer = setTimeout(() => this.closeAllDropdowns(), 40);
            });
            this.header.addEventListener('pointerenter', () => clearTimeout(leaveTimer));

            // 클릭 차단
            this.shopBtn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); });
            this.dBtns.forEach(btn => btn.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); }));
        }
    }

    openLnb() {
        this.closeNotice();
        this.lnb.classList.add('is-open');
        this.shopBtn.classList.add('is-open');
        this.headerBg.classList.add('is-active');
    }

    closeLnb() {
        this.lnb?.classList.remove('is-open');
        this.shopBtn?.classList.remove('is-open');
        this.headerBg.classList.remove('is-active');
    }

    openNotice(btn) {
        this.closeLnb();
        this.notice.classList.add('is-open');
        this.dBtns.forEach(b => b.classList.toggle('is-open', b === btn));
        this.headerBg.classList.add('is-active');
    }

    closeNotice() {
        this.notice?.classList.remove('is-open');
        this.dBtns?.forEach(b => b.classList.remove('is-open'));
        this.headerBg.classList.remove('is-active');
    }

    closeAllDropdowns() {
        this.closeLnb();
        this.closeNotice();
    }
}

customElements.define('sticky-header', StickyHeader);


/**
 * Mobile Menu Panel
 * 모바일 메뉴 패널 토글 및 탭 전환
 */
class MobileMenu {
    constructor() {
        this.menuBtn = document.querySelector('.m-nav-btn');
        this.panel = document.querySelector('.m-panel');
        this.panelDim = document.querySelector('.m-panel-dim');
        this.tabs = document.querySelectorAll('.mp-tab');
        this.contents = {
            shop: document.querySelector('.m-lnb'),
            notice: document.querySelector('.m-lnb-notice')
        };

        this.init();
    }

    init() {
        this.bindMenuToggle();
        this.bindTabSwitch();
    }

    bindMenuToggle() {
        this.menuBtn?.addEventListener('click', () => this.openMenu());
        this.panelDim?.addEventListener('click', () => this.closeMenu());
    }

    openMenu() {
        this.panel?.classList.add('show');
        document.body.classList.add('sc-p');
    }

    closeMenu() {
        this.panel?.classList.remove('show');
        document.body.classList.remove('sc-p');
    }

    bindTabSwitch() {
        this.tabs.forEach(tab => {
            tab.addEventListener('click', () => this.switchTab(tab));
        });
    }

    switchTab(clickedTab) {
        this.tabs.forEach(tab => tab.classList.remove('is-active'));
        clickedTab.classList.add('is-active');

        Object.values(this.contents).forEach(content => {
            content?.classList.remove('is-active');
        });

        if (clickedTab.classList.contains('m-shop-btn')) {
            this.contents.shop?.classList.add('is-active');
        } else if (clickedTab.classList.contains('mp-tab--d')) {
            this.contents.notice?.classList.add('is-active');
        }
    }
}

/**
 * Footer Info Toggle
 */
class FooterInfo {
    constructor() {
        this.footer = document.querySelector('.footer');
        this.footerInfo = document.querySelector('.footer-info');
        this.openBtn = document.querySelector('.footer-info-open-btn');
        this.closeBtn = document.querySelector('.footer-info-close-btn');
        this.closeBtnM = document.querySelector('.footer-info-close-btn--m');

        this.init();
    }

    init() {
        this.openBtn?.addEventListener('click', () => {
            this.footerInfo?.classList.add('is-open');
            this.footer?.classList.add('is-open');
        });

        this.closeBtn?.addEventListener('click', () => {
            this.footerInfo?.classList.remove('is-open');
            this.footer?.classList.remove('is-open');
        });

        this.closeBtnM?.addEventListener('click', () => {
            this.footerInfo?.classList.remove('is-open');
            this.footer?.classList.remove('is-open');
        });
    }
}

// DOM Ready
document.addEventListener('DOMContentLoaded', function() {
    new MobileMenu();
    new FooterInfo();
});